import type { components } from '@/types/apiSchema';

type WorkExperience = Pick<
  components['schemas']['CandidateWorkExperienceResponse'],
  'companyName' | 'startDate' | 'endDate'
>;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Months since year 0, so two dates subtract to whole months. */
const monthIndex = (value: string | Date) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? undefined
    : date.getUTCFullYear() * 12 + date.getUTCMonth();
};

/**
 * Months of work history, counting overlapping roles once: a side project
 * next to a full-time job does not add years. Roles without a valid start
 * date are skipped; undefined when none has one.
 */
export function experienceMonths(
  experiences: WorkExperience[],
  now: Date = new Date(),
): number | undefined {
  const nowIndex = monthIndex(now)!;
  const spans = experiences
    .flatMap(({ startDate, endDate }) => {
      const start = startDate ? monthIndex(startDate) : undefined;
      const end = endDate ? monthIndex(endDate) : nowIndex;
      if (start === undefined || end === undefined || end < start) return [];
      return [[start, Math.min(end, nowIndex)] as const];
    })
    .sort((a, b) => a[0] - b[0]);
  const [first, ...rest] = spans;
  if (!first) return undefined;

  let total = 0;
  let [spanStart, spanEnd] = first;
  for (const [start, end] of rest) {
    if (start <= spanEnd) {
      spanEnd = Math.max(spanEnd, end);
    } else {
      total += spanEnd - spanStart;
      [spanStart, spanEnd] = [start, end];
    }
  }
  return total + spanEnd - spanStart;
}

/**
 * Months at the current employer in the current stint: earlier roles there
 * count while they run up to it (a promotion does not reset tenure), but a
 * stint before leaving and coming back does not. Undefined without an open
 * role with a start date.
 */
export function currentCompanyMonths(
  experiences: WorkExperience[],
  now: Date = new Date(),
): number | undefined {
  const nowIndex = monthIndex(now)!;
  const current = experiences.find((experience) => !experience.endDate);
  let stintStart = current?.startDate
    ? monthIndex(current.startDate)
    : undefined;
  if (!current || stintStart === undefined) return undefined;

  const earlierRoles = experiences
    .filter(
      (experience) =>
        experience !== current &&
        experience.companyName === current.companyName,
    )
    .flatMap(({ startDate, endDate }) => {
      const start = startDate ? monthIndex(startDate) : undefined;
      const end = endDate ? monthIndex(endDate) : nowIndex;
      return start === undefined || end === undefined ? [] : [{ start, end }];
    })
    .sort((a, b) => b.start - a.start);
  // Newest first, each role extends the stint if it ends within a month
  // of where the stint starts.
  for (const role of earlierRoles) {
    if (role.end >= stintStart - 1)
      stintStart = Math.min(stintStart, role.start);
  }
  return Math.max(0, nowIndex - stintStart);
}

/** "6 yrs": whole years, for comparing seniority at a glance. */
export function formatYears(months: number): string {
  if (months < 12) return '<1 yr';
  const years = Math.floor(months / 12);
  return `${years} ${years === 1 ? 'yr' : 'yrs'}`;
}

/** "2 yrs 4 mo": months still matter for a tenure. */
export function formatTenure(months: number): string {
  const years = Math.floor(months / 12);
  const rest = months % 12;
  if (years === 0) return rest === 0 ? '<1 mo' : `${rest} mo`;
  const yearsLabel = `${years} ${years === 1 ? 'yr' : 'yrs'}`;
  return rest === 0 ? yearsLabel : `${yearsLabel} ${rest} mo`;
}

/** "Today", "5d", "3w", "4mo": how long someone has sat in their stage. */
export function formatStageAge(
  enteredAt: string,
  now: Date = new Date(),
): string | undefined {
  const entered = new Date(enteredAt).getTime();
  if (Number.isNaN(entered)) return undefined;
  const days = Math.max(0, Math.floor((now.getTime() - entered) / DAY_MS));
  if (days === 0) return 'Today';
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}
